import { motion } from "framer-motion";
import { Quote } from "lucide-react";

const FOUNDER = {
  name: "Mr. Thomas John",
  role: "Founder & CEO",
  image: "/assets/thomas-john.jpg",
  imagePosition: "object-top md:object-center",
  bio: [
    "Thomas John is the Founder and CEO of Catalyst Global Partners — a distinguished financial services consultancy recognized for delivering strategic funding solutions across India with over 28 years of specialized experience in finance and credit underwriting. He holds a double master's degree in Finance and Marketing. Thomas has built a strong foundation in his tenure at leading global organizations such as Biz2Credit, Genpact, Accenture, Aditya Birla Group, Hindustan Times, ING Bank and many more.",
    "His expertise spans complex credit evaluation, risk structuring, and tailored financial advisory — enabling businesses to access optimized funding solutions. Following his corporate success, Thomas advanced into financial consultancy, where he served as a Managing Director with several corporates and established a strong presence across Delhi NCR and on a pan-India scale and worldwide.",
    "Driven by entrepreneurial vision and a deep understanding of market gaps, Thomas made a strategic transition by founding Catalyst Global Partners — an independent consultancy built on trust, precision, and execution excellence. Under his leadership, Catalyst Global Partners has evolved into a well-recognized name in the Delhi NCR financial ecosystem, known for its strong lender relationships, high conversion capability, and client-centric approach.",
    "Renowned for his credibility, strategic insight, and results-driven mindset, Thomas John stands as a trusted leader shaping the future of business financing.",
  ],
  quote:
    "To redefine accessibility to capital — by creating a seamless, transparent and efficient funding environment for businesses of all sizes.",
};

const COFOUNDERS = [
  {
    name: "Mr. Aamitt Chopraa",
    role: "Co-Founder",
    image: "/assets/aamitt-chopra.jpg",
    imagePosition: "object-center",
    bio: [
      "Aamitt Chopraa is a Co-Founder of Catalyst Global Partners. He is a dynamic & creative businessman recognized for his significant contributions in architecture & manufacturing sector. He holds a Bachelor of Architecture degree. With a strong entrepreneurial vision and deep industry insight, he has built and led successful manufacturing operations focused on quality, efficiency, and sustainable growth.",
      "Over the years, Aamitt Chopra has demonstrated excellence in developing robust production systems, optimizing supply chains, and driving innovation across engineering, industrial, and various architectural projects through his contribution to D.M.R.C (Delhi Metro Railway Corporation). His leadership is marked by a strategic approach to scaling businesses, improving operational performance, and adapting to evolving market demands.",
      "He is known for fostering long-term partnerships, maintaining high standards of corporate governance, and delivering consistent value to stakeholders. Under his guidance, manufacturing enterprises have achieved enhanced productivity, market expansion, and competitive positioning both domestically and internationally.",
      "With a commitment to excellence and forward-thinking leadership & creative & innovative thinking, Aamitt Chopraa continues to play a pivotal role in strengthening the manufacturing landscape and contributing to industrial & architectural domain.",
    ],
  },
  {
    name: "Mr. Harinder Saini",
    role: "Co-Founder",
    image: "/assets/harinder-saini.jpg",
    imagePosition: "object-top md:object-center",
    bio: [
      "Harinder Saini is a Co-Founder of Catalyst Global Partners, specializing in Direct and Indirect Taxation with a focus on delivering strategic, compliant, and value-driven financial solutions. He holds a Bachelor of Commerce and an LLB degree. He is currently serving as Sr. Vice President of the District Tax Bar Association — Faridabad (Haryana).",
      "He has extensive experience in advising businesses on tax planning, regulatory matters, and transaction structuring — ensuring optimized financial outcomes in a dynamic regulatory environment. His role extends to facilitating debt and equity funding, managing stressed asset opportunities, and providing cross-sector business advisory services.",
      "Known for his analytical approach and practical execution, he supports clients in achieving operational efficiency, financial clarity, and long-term growth.",
      "He plays a key role in strengthening the firm's financial and compliance framework by ensuring every solution aligns with current regulatory standards and industry best practices. His ability to simplify complex taxation and legal structures enables businesses to make informed and confident financial decisions.",
    ],
  },
];

export default function Leadership() {
  return (
    <section
      id="leadership"
      data-testid="leadership-section"
      className="bg-[#FAFAF7] py-24 md:py-32"
    >
      <div className="cgp-container">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
          className="max-w-3xl"
        >
          <span className="cgp-overline">Leadership</span>
          <h2 className="font-serif font-light text-[#0A1F44] mt-6 text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
            The people behind
            <br />
            <span className="italic text-[#0A1F44]/70">the partnership.</span>
          </h2>
        </motion.div>

        {/* Founder */}
        <motion.div
          initial={{ opacity: 0, y: 28 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.7 }}
          data-testid="founder-card"
          className="mt-20 grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-14 items-start"
        >
          <div className="md:col-span-5 md:sticky md:top-28">
            <div className="relative overflow-hidden">
              <img
                src={FOUNDER.image}
                alt={FOUNDER.name}
                className={`w-full h-80 md:h-[560px] object-cover ${FOUNDER.imagePosition} grayscale`}
              />
              <span className="absolute bottom-0 left-0 right-0 h-1 bg-[#C8A24A]" />
            </div>
          </div>
          <div className="md:col-span-7">
            <span className="cgp-overline no-line">{FOUNDER.role}</span>
            <h3 className="font-serif text-4xl sm:text-5xl text-[#0A1F44] mt-4 font-light">
              {FOUNDER.name}
            </h3>
            <span className="cgp-gold-rule mt-5 block" />
            {FOUNDER.bio.map((p, i) => (
              <p
                key={i}
                className="text-[#0B1A36]/75 leading-relaxed mt-5 text-base md:text-[1.05rem]"
              >
                {p}
              </p>
            ))}
            <div className="mt-10 flex gap-5 items-start text-[#0A1F44]">
              <Quote size={28} className="text-[#C8A24A] shrink-0" />
              <p className="font-serif italic text-xl md:text-2xl leading-snug text-[#0A1F44]/85">
                “{FOUNDER.quote}”
              </p>
            </div>
          </div>
        </motion.div>

        {/* Co-founders — full-width stacked cards for room to render long bios */}
        <div className="mt-28 space-y-12 md:space-y-16">
          {COFOUNDERS.map((c, i) => (
            <motion.article
              key={c.name}
              initial={{ opacity: 0, y: 28 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.65, delay: i * 0.05 }}
              data-testid={`cofounder-card-${i}`}
              className="bg-white border border-[#0A1F44]/10 p-8 md:p-12 cgp-card-hover"
            >
              <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 items-start">
                <div className="md:col-span-5 md:sticky md:top-28">
                  <div className="relative overflow-hidden">
                    <img
                      src={c.image}
                      alt={c.name}
                      className={`w-full h-80 md:h-[560px] object-cover ${c.imagePosition || "object-center"} grayscale`}
                    />
                    <span className="absolute bottom-0 left-0 right-0 h-1 bg-[#C8A24A]" />
                  </div>
                </div>
                <div className="md:col-span-7">
                  <span className="cgp-overline no-line">{c.role}</span>
                  <h4 className="font-serif text-3xl md:text-4xl text-[#0A1F44] mt-3 font-light">
                    {c.name}
                  </h4>
                  <span className="cgp-gold-rule mt-5 block" />
                  {c.bio.map((p, idx) => (
                    <p
                      key={idx}
                      className="text-[#0B1A36]/75 leading-relaxed mt-5 text-base md:text-[1.05rem]"
                    >
                      {p}
                    </p>
                  ))}
                </div>
              </div>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
}
